/**
 * AddTileDrawer — picker + param form for adding a new tile.
 *
 * Core-flows §4: two levels — picker list, then per-type param form.
 * Per-type forms live in tile-forms.tsx (split for file size).
 *
 * Only shows free-tier, instantiable tile types in Phase 1.
 * Immutability: state replaced wholesale on every change.
 *
 * A11y (deliverable 6, L4):
 *   - Autofocuses the first control on open.
 *   - Traps Tab within the dialog (forward + backward).
 *   - Closes on Escape.
 *   - Restores focus to the trigger element on close.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { TileType } from 'briefpane/schema';
import type { Tile } from 'briefpane/schema';
import {
  WeatherForm,
  NewsForm,
  MarketsForm,
  SportsForm,
  ClockForm,
  CalendarForm,
  NoteForm,
  TodoForm,
  CountdownForm,
  RssForm,
} from './tile-forms.js';

interface AddTileDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Called when the user completes the form and a valid Tile is ready to add. */
  readonly onAdd: (tile: Tile) => void;
}

// Instantiable free-tier types (Phase 1/2)
const ADDABLE_TYPES: TileType[] = [
  'weather.current',
  'news.headlines',
  'markets.quote',
  'sports.score',
  'clock.local',
  'calendar.peek',
  // Local utility widgets (D-015)
  'note.sticky',
  'todo.list',
  'countdown.event',
  // User-source (ADR-005)
  'rss.feed',
];

const TYPE_LABELS: Record<string, string> = {
  'weather.current': 'Weather',
  'news.headlines': 'News',
  'markets.quote': 'Markets',
  'sports.score': 'Sports',
  'clock.local': 'Clock',
  'calendar.peek': 'Calendar',
  // Local utility widgets (D-015)
  'note.sticky': 'Note',
  'todo.list': 'To-do list',
  'countdown.event': 'Countdown',
  // User-source (ADR-005)
  'rss.feed': 'RSS feed',
};

/** H3: detect prefers-reduced-motion */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Query all focusable elements within a container.
 * Used for tab-trap implementation.
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

export function AddTileDrawer({ isOpen, onClose, onAdd }: AddTileDrawerProps): React.ReactElement | null {
  const [selectedType, setSelectedType] = useState<TileType | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Remember the element that had focus before the drawer opened.
  const triggerRef = useRef<Element | null>(null);

  // Swipe-to-dismiss state (SHOULD #10)
  const swipeStartYRef = useRef<number | null>(null);
  const [swipeTranslateY, setSwipeTranslateY] = useState(0);

  // Reset swipe state when open/close changes.
  useEffect(() => {
    setSwipeTranslateY(0);
    swipeStartYRef.current = null;
  }, [isOpen]);

  // On open: remember trigger element, autofocus first control.
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      // Allow React to paint the dialog before focusing
      const frame = requestAnimationFrame(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = getFocusableElements(dialog);
        if (focusable.length > 0) focusable[0]?.focus();
      });
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [isOpen]);

  // On close: restore focus to trigger element.
  useEffect(() => {
    if (!isOpen && triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Escape key: close the drawer.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
        return;
      }

      // Tab trap: keep focus within the dialog.
      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = getFocusableElements(dialog);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          // Backward tab: if focus is on first element, wrap to last.
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          // Forward tab: if focus is on last element, wrap to first.
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function handleAdd(tile: Tile): void {
    onAdd(tile);
    setSelectedType(null);
    onClose();
  }

  function handleClose(): void {
    setSelectedType(null);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      aria-hidden={false}
      className="sheet-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-end',
        backgroundColor: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add a tile"
        style={{
          background: 'var(--color-bg-tile)',
          borderRadius: 'var(--radius-drawer)',
          width: '100%',
          maxWidth: '480px',
          margin: '0 auto',
          padding: 'var(--space-5)',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-drawer)',
          transform: `translateY(${swipeTranslateY}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => {
          // Only primary touch/mouse
          if (e.button !== 0) return;
          // H2: Don't hijack scrolled content (scroll guard)
          const el = dialogRef.current;
          if (el && el.scrollTop > 0) return;
          // H3: no tracking under reduced motion
          if (prefersReducedMotion()) {
            swipeStartYRef.current = null;
            return;
          }
          swipeStartYRef.current = e.clientY;
          setSwipeTranslateY(0);
        }}
        onPointerMove={(e) => {
          if (swipeStartYRef.current === null) return;
          // H2: If content has scrolled since we started tracking, abort
          const el = dialogRef.current;
          if (el && el.scrollTop > 0) {
            swipeStartYRef.current = null;
            setSwipeTranslateY(0);
            return;
          }
          const deltaY = e.clientY - swipeStartYRef.current;
          if (deltaY > 0) setSwipeTranslateY(deltaY);
        }}
        onPointerUp={(e) => {
          if (swipeStartYRef.current === null) return;
          const deltaY = e.clientY - swipeStartYRef.current;
          swipeStartYRef.current = null;
          // H3: under reduced motion, instant close if threshold met, otherwise nothing
          if (prefersReducedMotion()) {
            const sheetHeight = dialogRef.current?.offsetHeight ?? 400;
            if (deltaY > sheetHeight * 0.3 || deltaY > 80) {
              handleClose();
            }
            return;
          }
          const sheetHeight = dialogRef.current?.offsetHeight ?? 400;
          const velocityPx = deltaY; // simplified: no actual velocity calc
          // Dismiss threshold: 30% of height OR > 120px quick swipe
          if (deltaY > sheetHeight * 0.3 || (deltaY > 80 && velocityPx > 80)) {
            setSwipeTranslateY(0);
            handleClose();
          } else {
            // Spring back
            setSwipeTranslateY(0);
          }
        }}
        onPointerCancel={() => {
          swipeStartYRef.current = null;
          setSwipeTranslateY(0);
        }}
      >
        {/* Grabber bar (§4.1): coarse-pointer / touch only. */}
        <div data-sheet-grabber aria-hidden="true" />

        {selectedType === null ? (
          <TypePicker
            types={ADDABLE_TYPES}
            labels={TYPE_LABELS}
            onSelect={setSelectedType}
            onClose={handleClose}
          />
        ) : (
          <TypeForm
            type={selectedType}
            label={TYPE_LABELS[selectedType] ?? selectedType}
            onBack={() => setSelectedType(null)}
            onSubmit={handleAdd}
          />
        )}
      </div>
    </div>
  );
}

interface TypePickerProps {
  readonly types: TileType[];
  readonly labels: Record<string, string>;
  readonly onSelect: (type: TileType) => void;
  readonly onClose: () => void;
}

function TypePicker({ types, labels, onSelect, onClose }: TypePickerProps): React.ReactElement {
  return (
    <>
      <h2 style={{ fontSize: 'var(--text-detail)', fontWeight: 600, margin: '0 0 var(--space-4)' }}>
        Add a tile
      </h2>
      <ul
        role="list"
        style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
      >
        {types.map((type) => (
          <li key={type}>
            <button
              type="button"
              onClick={() => onSelect(type)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-3)',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-button)',
                cursor: 'pointer',
                fontSize: 'var(--text-detail)',
                color: 'var(--color-text-primary)',
                fontWeight: 500,
              }}
            >
              {labels[type] ?? type}
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 'var(--space-4)',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-dim)',
          fontSize: 'var(--text-label)',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        Cancel
      </button>
    </>
  );
}

interface TypeFormProps {
  readonly type: TileType;
  readonly label: string;
  readonly onBack: () => void;
  readonly onSubmit: (tile: Tile) => void;
}

function TypeForm({ type, label, onBack, onSubmit }: TypeFormProps): React.ReactElement {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <button
          type="button"
          aria-label="Back to tile list"
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-dim)',
            fontSize: 'var(--text-detail)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ← Back
        </button>
        <h2 style={{ fontSize: 'var(--text-detail)', fontWeight: 600, margin: 0 }}>{label}</h2>
      </div>

      {type === 'weather.current' && <WeatherForm onSubmit={onSubmit} />}
      {type === 'news.headlines' && <NewsForm onSubmit={onSubmit} />}
      {type === 'markets.quote' && <MarketsForm onSubmit={onSubmit} />}
      {type === 'sports.score' && <SportsForm onSubmit={onSubmit} />}
      {type === 'clock.local' && <ClockForm onSubmit={onSubmit} />}
      {type === 'calendar.peek' && <CalendarForm onSubmit={onSubmit} />}
      {type === 'note.sticky' && <NoteForm onSubmit={onSubmit} />}
      {type === 'todo.list' && <TodoForm onSubmit={onSubmit} />}
      {type === 'countdown.event' && <CountdownForm onSubmit={onSubmit} />}
      {type === 'rss.feed' && <RssForm onSubmit={onSubmit} />}
    </>
  );
}
